import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Check, X, AlertCircle, Shield, Trash2, Info } from 'lucide-react';

export type ModalType = 'confirm' | 'danger' | 'warning' | 'success';

interface ModalProps {
  isOpen: boolean;
  type?: ModalType;
  icon?: React.ReactNode;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  type = 'confirm',
  icon,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onCancel]);

  const config = {
    confirm: { bg: 'bg-[#EEF2FF]', iconColor: 'text-[#4F46E5]', icon: <Shield size={24} /> },
    danger: { bg: 'bg-[#FEF2F2]', iconColor: 'text-[#EF4444]', icon: <Trash2 size={24} /> },
    warning: { bg: 'bg-[#FFFBEB]', iconColor: 'text-[#F59E0B]', icon: <AlertCircle size={24} /> },
    success: { bg: 'bg-[#ECFDF5]', iconColor: 'text-[#10B981]', icon: <Check size={24} /> },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel}
            className="absolute inset-0 bg-black/45 backdrop-blur-[4px]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 8 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-2xl w-full max-w-[460px] max-h-[calc(100vh-48px)] overflow-y-auto p-7 shadow-[0_20px_60px_rgba(0,0,0,0.25),0_8px_24px_rgba(0,0,0,0.12)]"
          >
            <div className="flex justify-center mb-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${config[type].bg} ${config[type].iconColor}`}>
                {icon || config[type].icon}
              </div>
            </div>
            <h3 className="text-[18px] font-bold text-[#0F172A] text-center mb-2">{title}</h3>
            <p className="text-[14px] text-[#64748B] text-center leading-relaxed mb-6">{message}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={onCancel}
                className="h-[42px] min-w-[120px] px-6 rounded-[10px] border border-[#E2E8F0] font-semibold text-sm text-[#374151] hover:bg-gray-50 transition-colors"
              >
                {cancelText}
              </button>
              <button
                onClick={() => {
                  onConfirm();
                  onCancel();
                }}
                className={`h-[42px] min-w-[120px] px-6 rounded-[10px] font-semibold text-sm text-white shadow-lg transition-all active:scale-95 ${
                  type === 'danger' ? 'bg-[#EF4444] shadow-red-500/20' : 'bg-[#4F46E5] shadow-primary/20'
                }`}
              >
                {confirmText}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
